import { z } from 'zod';

/**
 * Server-side conversions accepted from the browser. 'Purchase' is NOT here on
 * purpose: purchase conversions must come from the Stripe webhook (trusted,
 * server-verified amount) so authenticated clients cannot forge them and
 * poison ad-platform conversion data.
 */
export const TRACKING_EVENT_NAMES = ['CompleteRegistration', 'StartTrial'] as const;
export type TrackingEventName = (typeof TRACKING_EVENT_NAMES)[number];

export const trackingEventNameSchema = z.enum(TRACKING_EVENT_NAMES);

// Cap client-reported conversion values so forged events cannot carry absurd amounts.
const MAX_CONVERSION_VALUE = 10_000_000;
// ISO 4217 currency codes are exactly 3 letters.
const CURRENCY_CODE_LENGTH = 3;

export const facebookEventInputSchema = z.object({
  eventName: trackingEventNameSchema,
  eventId: z.string().optional(),
  eventTime: z.number().optional(),
  eventSourceUrl: z.string().url().optional(),
  actionSource: z.string().default('website'),
  value: z.number().positive().max(MAX_CONVERSION_VALUE).optional(),
  currency: z.string().length(CURRENCY_CODE_LENGTH).optional(),
  email: z.string().email().optional(),
  clientUserAgent: z.string().optional(),
});

export type FacebookEventInput = z.infer<typeof facebookEventInputSchema>;

export const gtmServerForwardSchema = z.object({
  payload: z
    .object({
      event: trackingEventNameSchema,
    })
    .passthrough(),
});

export type GtmServerForwardInput = z.infer<typeof gtmServerForwardSchema>;

export const tiktokEventInputSchema = z.object({
  pixelCode: z.string(),
  event: trackingEventNameSchema,
  eventId: z.string(),
  timestamp: z.string().optional(),
  context: z.object({
    ad: z
      .object({
        callback: z.string().optional(),
      })
      .optional(),
    page: z
      .object({
        url: z.string().url(),
      })
      .optional(),
    userAgent: z.string().optional(),
  }),
  user: z.object({
    email: z.string().email().optional(),
  }),
  properties: z
    .object({
      value: z.number().positive().max(MAX_CONVERSION_VALUE).optional(),
      currency: z.string().length(CURRENCY_CODE_LENGTH).optional(),
    })
    .optional(),
  testEventCode: z.string().optional(),
});

export type TiktokEventInput = z.infer<typeof tiktokEventInputSchema>;
